// libraries
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
	Button,
	Form,
	FormInstance,
	Input,
	Modal,
	notification,
	Select,
	Switch,
	Tooltip,
} from 'antd';
import { ArrowRightOutlined, CheckOutlined, DeleteFilled, SwapOutlined } from '@ant-design/icons';
import { FieldData } from 'rc-field-form/lib/interface';

// services
import * as UnionService from '../../../../services/bb-api/union';
import * as MunicipalityService from '../../../../services/bb-api/municipality';
import * as StatusService from '../../../../services/bb-api/status';

// resources
import { txt } from '../../../../locales/i18n';
import { ImageTool } from '../../../shared/image-tool';
import { DeleteModal } from '../../../shared/delete-modal';
import { useDeviceSize } from '../../../../hooks/device-size';
import { AddressSearchBar, Address } from '../../../shared/address-search-bar';

export type UnionPageFormProps = {
	useUnion: [Record<string, any>, (union: Record<string, any>) => void];
};

export function UnionPageForm(props: UnionPageFormProps): JSX.Element {
	const [union, setUnion] = props.useUnion;
	const [imageSource, setImageSource] = useState<string>(String(union.image));
	const [initialImageSource, setInitialImageSource] = useState<string>();
	const [loading, setLoading] = useState<boolean>(true);
	const isMobileDevice: boolean = useDeviceSize();

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showMissingCoordsModal, setShowMissingCoordsModal] = useState(false);
	const [showMoveToPostModal, setShowMoveToPostModal] = useState(false);
	const [ignoreCoordinates, setIgnoreCoordinates] = useState(false);

	const [statuses, setStatuses] = useState<Record<string, any>[]>([]);
	const [municipalities, setMunicipalities] = useState<Record<string, any>[]>([]);

	const formRef = useRef<FormInstance>(null);
	const imageToolRef = useRef<any>(null);

	const { id } = useParams<any>();
	const history = useHistory();

	useEffect(() => {
		async function getResources(): Promise<void> {
			setLoading(true);

			try {
				const statuses: Record<string, any>[] = await StatusService.query();
				const municipalities: Record<string, any>[] = await MunicipalityService.query();

				setStatuses(statuses);
				setMunicipalities(municipalities);
			} catch (error) {
				const message: string = (error as Error).name;
				const description: string = (error as Error).message;
				notification.error({ message, description });
				history.replace('/unions');
			}

			setLoading(false);
		}

		getResources();
	}, [id, history]);

	function handleNavigateLink(): void {
		const link: string | null = formRef?.current?.getFieldValue('url');

		if (link === null) return;

		if (/^http/g.test(link)) {
			window?.open(link, '_blank')?.focus();
			return;
		}

		window?.open('http://' + link, '_blank')?.focus();
	}

	async function handleUnionUpdate(values: any): Promise<void> {
		const imageTool = imageToolRef?.current;
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		if (imageTool === null) {
			notification.error({ message: txt('notification.image missing') });
			return;
		}

		const canvas = imageTool.getImage();
		const base64: string = canvas.toDataURL('image/jpeg');

		values.image = base64;
		values.updateImage = base64 !== initialImageSource;

		const lat = form.getFieldValue('lat') ?? null;

		if (lat === null && ignoreCoordinates === false) {
			setShowMissingCoordsModal(true);
			return;
		}

		setLoading(true);

		try {
			await UnionService.update(id, { ...union, ...values });
			setUnion({ ...union, ...values });
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setIgnoreCoordinates(false);
		setLoading(false);
		history.goBack();
	}

	async function handleDelete(): Promise<void> {
		await UnionService.remove(id);
		history.replace('/unions');
	}

	const handleAddressSelect = (address: Address | null) => {
		const form = formRef.current;
		if (form === null) return;

		if (address === null) {
			const addressFields: FieldData[] = [
				{ name: 'city', value: null },
				{ name: 'address', value: null },
				{ name: 'postalCode', value: null },
				{ name: 'lat', value: null },
				{ name: 'lng', value: null },
			];

			return form?.setFields(addressFields);
		}

		const addressFields: FieldData[] = [
			{ name: 'city', value: address.city },
			{ name: 'address', value: address.name },
			{ name: 'postalCode', value: address.postalCode },
			{ name: 'lat', value: address.lat },
			{ name: 'lng', value: address.lng },
		];

		form?.setFields(addressFields);
	};

	const handleIgnoreCoordinates = () => {
		setIgnoreCoordinates(true);
		setShowMissingCoordsModal(false);
	};

	return (
		<>
			<StyledForm
				ref={formRef}
				initialValues={union}
				onFinish={handleUnionUpdate}
				labelCol={{ span: 4 }}
				isMobileDevice={isMobileDevice}
			>
				<div className="form-content-container">
					<Form.Item label={txt('global.title')} name="title">
						<Input required />
					</Form.Item>

					<Form.Item label="Status" name="status">
						<Select dropdownMatchSelectWidth={false}>
							{statuses.map((status: Record<string, any>) => (
								<Select.Option key={status.id} value={status.id}>
									{status.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('global.text')} name="body">
						<Input.TextArea required />
					</Form.Item>

					<Form.Item label={txt('global.image')}>
						<ImageTool
							refProxy={imageToolRef}
							imageSource={imageSource}
							setImageSource={setImageSource}
							initialImageSource={initialImageSource}
							setInitialImageSource={setInitialImageSource}
						/>
					</Form.Item>

					<Form.Item label={txt('global.search')}>
						<AddressSearchBar onSelect={handleAddressSelect} />
					</Form.Item>

					<Form.Item name="address" label={txt('global.address')}>
						<Input placeholder={txt('global.address')} disabled />
					</Form.Item>

					<Form.Item label={txt('global.city')}>
						<div className="multi-input-container">
							<Form.Item name="postalCode" noStyle>
								<Input
									placeholder={txt('global.postal code')}
									disabled
									className="postalcode-input"
								/>
							</Form.Item>

							<Form.Item name="city" noStyle>
								<Input placeholder={txt('global.city')} disabled />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item label="GPS">
						<div className="multi-input-container">
							<Form.Item name="lat" noStyle>
								<Input disabled placeholder="latitude" />
							</Form.Item>

							<Form.Item name="lng" noStyle>
								<Input disabled placeholder="longitude" />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item label={txt('global.municipality')} name="municipalityId">
						<Select dropdownMatchSelectWidth={false}>
							{municipalities.map((municipality: Record<string, any>) => (
								<Select.Option key={municipality.id} value={municipality.id}>
									{municipality.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('global.website')}>
						<Input.Group compact style={{ display: 'flex' }}>
							<Form.Item name="url" style={{ flex: 1 }} noStyle>
								<Input />
							</Form.Item>

							<Form.Item noStyle>
								<Tooltip title={txt('global.open')}>
									<Button type="link" onClick={handleNavigateLink}>
										<ArrowRightOutlined />
									</Button>
								</Tooltip>
							</Form.Item>
						</Input.Group>
					</Form.Item>
				</div>

				<div className="form-submit-container">
					<Form.Item name="approved" valuePropName="checked">
						<Switch
							checkedChildren={txt('global.approved')}
							unCheckedChildren={txt('global.not approved')}
							defaultChecked={union.approved === 1}
						/>
					</Form.Item>

					<Tooltip visible={ignoreCoordinates} title={txt('unions.missing coordinates')}>
						<Button
							size={isMobileDevice ? 'large' : 'middle'}
							type="primary"
							htmlType="submit"
							loading={loading}
						>
							{txt('global.update')} <CheckOutlined />
						</Button>
					</Tooltip>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="link"
						href={`https://app.boblberg.dk/forening/${union.id}`}
						target="_blank"
					>
						{txt('global.open')} <ArrowRightOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						onClick={() => setShowMoveToPostModal(true)}
						className="move-to-posts-button"
					>
						{txt('unions.move to posts')} <SwapOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						danger
						type="ghost"
						onClick={() => setShowDeleteModal(true)}
					>
						{txt('global.delete')} <DeleteFilled />
					</Button>
				</div>
			</StyledForm>

			<Modal
				okText={txt('global.continue')}
				cancelText={txt('global.cancel')}
				onOk={handleIgnoreCoordinates}
				onCancel={() => setShowMissingCoordsModal(false)}
				visible={showMissingCoordsModal}
				closable={false}
				maskClosable
			>
				<h2>{txt('unions.missing coordinates')}</h2>
				<p>{txt('unions.missing coordinates body 1')}</p>

				<p>
					<i>{txt('unions.missing coordinates body 2')}</i>
				</p>
			</Modal>

			<DeleteModal
				useVisible={[showDeleteModal, setShowDeleteModal]}
				onConfirm={handleDelete}
			/>

			<MoveToPostModal
				unionId={union.id}
				municipality={union.municipalityId}
				useVisibility={[showMoveToPostModal, setShowMoveToPostModal]}
				useLoading={[loading, setLoading]}
			/>
		</>
	);
}

function MoveToPostModal(props: any) {
	const [loading, setLoading] = props.useLoading;
	const [visible, setVisible] = props.useVisibility;
	const history = useHistory();

	async function handleMoveToPost(): Promise<void> {
		setLoading(true);

		try {
			const postId: number = await UnionService.moveToPost(props.unionId, props.municipality);
			notification.success({ message: txt('notification.update successful') });
			setVisible(false);
			history.replace(`/posts/${postId}`);
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	return (
		<Modal
			okText={txt('global.move')}
			cancelText={txt('global.cancel')}
			onOk={handleMoveToPost}
			onCancel={() => setVisible(false)}
			visible={visible}
			confirmLoading={loading}
			closable={false}
			maskClosable
		>
			<p>{txt('unions.move to posts')}</p>
		</Modal>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	display: flex;
	flex-direction: column;
	gap: 2em;

	.form-content-container {
		display: grid;
		gap: 0rem 2rem;

		${({ isMobileDevice }) => {
			if (isMobileDevice) return;
			return 'grid-template-columns: repeat(auto-fit, minmax(max(450px, 40%), 1fr));';
		}}

		textarea {
			box-sizing: border-box;
			min-height: 280px;
			width: 100%;
			resize: none;
		}

		.multi-input-container {
			display: flex;

			> * {
				flex: 1;
			}

			.postalcode-input {
				width: 12ch;
				flex: none;
			}
		}
	}

	.form-submit-container {
		display: flex;
		flex-direction: row;
		gap: 1em;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		button.ant-switch {
			zoom: 1.4;
			> span {
				font-size: 0.65em;
			}
		}

		.move-to-posts-button {
			margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		}
	}
`;
